import { useState } from "react";
import { Link } from "react-router-dom";
import { MailCheck, Users } from "lucide-react";
import { useMutation } from "@apollo/client/react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ScioMark } from "@/components/ui/scio-mark";
import { PublicHeader } from "@/components/layout/public-header";
import { LegalFooter } from "@/components/legal/legal-footer";
import { REGISTER_WITH_EMAIL } from "@/graphql/mutations/account";
import { ChildDataConsent, TermsConsent } from "@/components/legal/terms-consent";
import { GuardianConfirmation } from "@/components/legal/guardian-confirmation";

/**
 * One kind of account, two ways to sign up.
 *
 * **Only a grown-up registers** (issue #55, GDPR art. 8): a child is a profile
 * the parent creates afterwards, never an account of its own, so there is no
 * role to choose here. Everything on this page is a convenience for the person
 * filling it in — the rules are enforced on the server, because the Google half
 * of this form is one editable URL: it leaves carrying `role`, `terms` and
 * `guardian` as query parameters and they come back on `omniauth.params`.
 * `RegistrationIntent` refuses anything but a guardian who ticked both boxes,
 * and `registerWithEmail` re-checks the identical rules for the other half.
 *
 * **Google is still the primary and it is not merely habit.** Google has already
 * verified the address, so that account arrives confirmed and can be used at
 * once; an address typed here is asserted, so it costs a confirmation email and
 * cannot be signed in with until the link is followed. The form says so before
 * anybody types a password, rather than after.
 */

/** What `registerWithEmail` answers. `emailed` is false when the account exists
 *  but the mail did not leave — which the page has to say rather than swallow. */
type RegisterResult = {
  registerWithEmail: { success: boolean; emailed: boolean; errors: string[] };
};

export default function RegisterPage() {
  // All three unticked to start with, for both ways in: both create an account
  // and both are refused on the server without them. Three boxes and not one,
  // because consent to a child's data may not be bundled with the terms
  // (issue #56).
  const [agreed, setAgreed] = useState(false);
  const [childData, setChildData] = useState(false);
  const [isGuardian, setIsGuardian] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  // Set once the account exists. The page then stops being a form: there is
  // nothing left to fill in and the next step is in their inbox, so offering the
  // fields again would invite a second account on a second address.
  const [sent, setSent] = useState<{ emailed: boolean } | null>(null);

  const [register, { loading }] = useMutation<RegisterResult>(REGISTER_WITH_EMAIL, {
    onCompleted: (data) => {
      const result = data.registerWithEmail;
      if (result.success) setSent({ emailed: result.emailed });
      else setErrors(result.errors);
    },
    // A network failure is not a rejected registration, and saying „patikrinkite
    // duomenis" would send them to correct a form that was fine.
    onError: () => setErrors(["Nepavyko susisiekti su serveriu. Bandykite dar kartą."]),
  });

  const ready = agreed && childData && isGuardian;

  /** Mirrors Student::MIN_PASSWORD_LENGTH, which is what actually decides. */
  const MIN_PASSWORD = 8;
  const emailReady =
    ready && name.trim().length > 0 && email.includes("@") && password.length >= MIN_PASSWORD;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    register({
      variables: {
        name: name.trim(),
        email: email.trim(),
        password,
        acceptsTerms: agreed,
        acceptsChildData: childData,
        confirmsGuardian: isGuardian,
      },
    });
  };

  const href = () => {
    // `terms=1`, `child_data=1` and `guardian=1` ride the Google round trip
    // like `role` does; without them the callback creates nothing
    // (RegistrationIntent#valid?).
    const params = new URLSearchParams({
      role: "guardian",
      terms: agreed ? "1" : "",
      child_data: childData ? "1" : "",
      guardian: isGuardian ? "1" : "",
    });
    return `/users/auth/google_oauth2?${params.toString()}`;
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-background">
        <PublicHeader action="register" />

        <div className="flex items-center justify-center px-4 pb-16 pt-4 sm:pt-10">
          <Card className="w-full max-w-md">
            <CardHeader className="space-y-1">
              <MailCheck className="mx-auto mb-2 h-11 w-11 text-primary" aria-hidden="true" />
              <CardTitle className="text-center text-2xl font-bold tracking-tight">
                Patikrinkite el. paštą
              </CardTitle>
              <CardDescription className="text-center">
                Nuorodą išsiuntėme adresu <strong>{email.trim()}</strong>. Paspauskite ją —
                ir galėsite prisijungti.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* The account exists whether or not the mail left, so a failure to
                  send has to be said out loud. Silence here would leave somebody
                  waiting for a link that was never sent, with nothing to press. */}
              {!sent.emailed && (
                <Alert variant="destructive">
                  <AlertDescription>
                    Laiško išsiųsti nepavyko. Paskyra sukurta — nuorodos galite paprašyti
                    dar kartą prisijungimo lange.
                  </AlertDescription>
                </Alert>
              )}

              <p className="text-sm text-muted-foreground">
                Laiško nėra? Pažiūrėkite šlamšto (spam) dėžutėje — o jei ir ten nėra,
                prisijungimo lange galėsite paprašyti naujos nuorodos.
              </p>

              <Button asChild className="w-full">
                <Link to="/auth/login">Eiti į prisijungimą</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
        <LegalFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader action="register" />

      <div className="flex items-center justify-center px-4 pb-16 pt-4 sm:pt-10">
        <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <ScioMark size={44} className="mx-auto mb-2 text-primary" title="Scio" />
          <CardTitle className="text-center text-2xl font-bold tracking-tight">
            Sukurkite paskyrą
          </CardTitle>
          <CardDescription className="text-center">
            Paskyrą kuria tėvai ar globėjai. Vaiko profilį sukursite po registracijos.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="rounded-xl border p-4">
            <Users className="h-5 w-5 text-primary" aria-hidden="true" />
            <span className="mt-2 block font-semibold">Tėvai ar globėjai</span>
            <span className="mt-0.5 block text-xs text-muted-foreground">
              Pridėsiu vaikus ir matysiu jų pažangą.
            </span>
          </div>

          {/* Above both buttons, because they govern both. */}
          <div className="space-y-3">
            <GuardianConfirmation checked={isGuardian} onChange={setIsGuardian} />
            <TermsConsent checked={agreed} onChange={setAgreed} />
            <ChildDataConsent checked={childData} onChange={setChildData} />
          </div>

          {/* A full page navigation, not a fetch: this hands the browser to
              Google and comes back at /users/auth/google_oauth2/callback.
              `disabled` is not a thing an <a> has — and `asChild` renders the
              anchor — so the unready state is styled and blocked explicitly
              rather than left looking pressable. */}
          <Button
            asChild
            className={`w-full ${ready ? "" : "pointer-events-none opacity-50"}`}
          >
            <a
              href={ready ? href() : undefined}
              aria-disabled={!ready}
              tabIndex={ready ? undefined : -1}
              onClick={(e) => {
                if (!ready) e.preventDefault();
              }}
            >
              Registruotis su Google
            </a>
          </Button>

          {!ready && (
            <p className="text-center text-xs text-muted-foreground">
              Pažymėkite visus langelius aukščiau.
            </p>
          )}

          {/* The other way in, shown rather than folded behind a link — the
              owner's call (issue #68), laid out as the login page is: Google
              first and filled, then „arba", then the form with an outlined
              button, so the two still do not look like equal choices. */}
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" />
            arba el. paštu
            <span className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            {errors.length > 0 && (
              <Alert variant="destructive">
                <AlertDescription>
                  {errors.map((message) => (
                    <span key={message} className="block">
                      {message}
                    </span>
                  ))}
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">Vardas</Label>
              <Input
                id="name"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">El. paštas</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="vardas@pastas.lt"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Slaptažodis</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                minLength={MIN_PASSWORD}
                required
              />
              <p className="text-xs text-muted-foreground">
                Bent {MIN_PASSWORD} simboliai.
              </p>
            </div>

            {/* Said before the password is typed, not after: it is the one way
                this differs from the Google button above, and finding out
                afterwards reads as the account being broken. */}
            <p className="text-xs text-muted-foreground">
              Į šį adresą išsiųsime patvirtinimo nuorodą. Kol jos nepaspausite,
              prisijungti su slaptažodžiu negalėsite.
            </p>

            <Button
              type="submit"
              variant="outline"
              className="w-full"
              disabled={loading || !emailReady}
            >
              {loading ? "Kuriama..." : "Sukurti paskyrą"}
            </Button>
            </form>

          <p className="text-center text-sm text-muted-foreground">
            Jau turite paskyrą?{" "}
            <Link to="/auth/login" className="underline underline-offset-4">
              Prisijungti
            </Link>
          </p>
          </CardContent>
        </Card>
      </div>
      <LegalFooter />
    </div>
  );
}
